import * as React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { DataSelectors, UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormModel, FormModelPath } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DefaultFormModelMap, FormModelMap } from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";
import { Tooltip } from "@com.mgmtp.a12.widgets/widgets-core/lib/tooltip";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { DisplayType, resolveDisplayType } from "../../../utilities/AnnotationHelper";
import {
	BUTTON_DEPENDENT_ENABLEMENT,
	RUNTIME_MODULE_CHECK_ROLES
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { isAllowedRuntimeModule } from "../../../utilities/user";
import { RESOURCE_KEYS } from "../../../localization";
import { Manifest } from "../../../app/slices/manifest/types";
import {
	CREATE_ZAHLUNGSVORGANG_EPAYBL,
	GET_ZAHLUNGSVORGANGSTATUS_EPAYBL
} from "../../../utilities/variables/customEventNames";

import { EventButtonContext } from "./contexts";

/** Stellt einen Kontext mit Informationen zum Button-Event bereit **/
export const CustomEventButton = (manifest?: Manifest) =>
	function CustomEventButton(props: FormModelMap.FormModelComponentProps<FormModel.EventButton>) {
		const { event: eventName } = props.modelElement;
		const parentPath = props.config.parentPath;
		const buttonPath = FormModelPath.extend(parentPath, props.modelElement);
		const document = DataSelectors.document()(props.config.renderOptions.state);
		const screenPath = UiStateSelectors.currentScreenLocation()(props.config.renderOptions.state).path;
		const { localizer } = React.useContext(LocalizerContext);
		const user = useSelector(UaaSelectors.user);
		const missingInboxReference = localizer({ key: RESOURCE_KEYS.features.postfach.error.missingInboxReference });
		const missingInboxReferenceLabel = localizer({
			key: RESOURCE_KEYS.features.postfach.error.missingInboxReferenceLabel
		});

		const display = checkButtonPermissions(props.modelElement, user, manifest)
			? getButtonDisplayType(props.modelElement, document, screenPath)
			: DisplayType.HIDE;
		const disabledReason = getDisabledReason(eventName, display, localizer);

		const eventButtonContext = React.useMemo(
			() => ({ eventName, a11yId: undefined, buttonPath: buttonPath, display, disabledReason }),
			[eventName, buttonPath, display, disabledReason]
		);

		return (
			<EventButtonContext.Provider value={eventButtonContext}>
				<StyledEventButtonWrapper>
					<DefaultFormModelMap.EventButton.component {...props} />
				</StyledEventButtonWrapper>
				{eventName === "event_send_mail_bund_id" && display === DisplayType.DISABLE && (
					<Tooltip text={missingInboxReference} variant="error">
						<Button
							type="button"
							icon={<Icon variant="error">error</Icon>}
							buttonAttributes={{ "aria-label": missingInboxReferenceLabel }}
						/>
					</Tooltip>
				)}
			</EventButtonContext.Provider>
		);
	};

const StyledEventButtonWrapper = styled.span`
	display: inline-block;
`;

function checkButtonPermissions(modelElement: FormModel.EventButton, user: object, manifest?: Manifest): boolean {
	const runtimeModuleCheckRolesAnnotation = modelElement.annotations?.find(
		e => e.name === RUNTIME_MODULE_CHECK_ROLES
	);

	return (
		!runtimeModuleCheckRolesAnnotation?.value ||
		isAllowedRuntimeModule(user, runtimeModuleCheckRolesAnnotation.value, manifest)
	);
}

function getButtonDisplayType(
	modelElement: FormModel.EventButton,
	document: object,
	screenPath: EntityInstancePath
): DisplayType {
	const buttonEnablement = modelElement.annotations?.find(
		a => a.name === BUTTON_DEPENDENT_ENABLEMENT && a.value
	)?.value;
	return buttonEnablement ? resolveDisplayType(document, screenPath, buttonEnablement) : DisplayType.SHOW;
}

function getDisabledReason(
	eventName: string | undefined,
	display: DisplayType,
	localizer: (props: { key: string }) => string | undefined
): string | undefined {
	if (display !== DisplayType.DISABLE) {
		return undefined;
	}

	switch (eventName) {
		case CREATE_ZAHLUNGSVORGANG_EPAYBL:
			return localizer({ key: RESOURCE_KEYS.features.zahlungsvorgangEpaybl.disabledReason.create });
		case GET_ZAHLUNGSVORGANGSTATUS_EPAYBL:
			return localizer({ key: RESOURCE_KEYS.features.zahlungsvorgangEpaybl.disabledReason.status });
		default:
			return localizer({ key: RESOURCE_KEYS.features.button.disabledReason });
	}
}
